import { useState } from "react";
import {
  ChartLine, Folders, CalendarBlank, ChartLineUp, CurrencyDollarSimple,
  Shield, Camera, FilePdf, WifiSlash, Palette, UsersThree, GraduationCap,
} from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger,
} from "@/components/ui/dialog";
import {
  Accordion, AccordionItem, AccordionTrigger, AccordionContent,
} from "@/components/ui/accordion";
import { ScrollArea } from "@/components/ui/scroll-area";
import { useLang } from "@/i18n";

const MODULES = [
  {
    key: "tutDashboard",
    icon: ChartLine,
    color: "bg-blue-100 text-blue-900",
    bulletsKey: "tutDashboardBullets",
  },
  {
    key: "tutProjects",
    icon: Folders,
    color: "bg-amber-100 text-amber-800",
    bulletsKey: "tutProjectsBullets",
  },
  {
    key: "tutGantt",
    icon: CalendarBlank,
    color: "bg-emerald-100 text-emerald-800",
    bulletsKey: "tutGanttBullets",
  },
  {
    key: "tutScurve",
    icon: ChartLineUp,
    color: "bg-violet-100 text-violet-800",
    bulletsKey: "tutScurveBullets",
  },
  {
    key: "tutCosts",
    icon: CurrencyDollarSimple,
    color: "bg-teal-100 text-teal-800",
    bulletsKey: "tutCostsBullets",
  },
  {
    key: "tutSafety",
    icon: Shield,
    color: "bg-red-100 text-red-800",
    bulletsKey: "tutSafetyBullets",
  },
  {
    key: "tutPhotos",
    icon: Camera,
    color: "bg-sky-100 text-sky-800",
    bulletsKey: "tutPhotosBullets",
  },
  {
    key: "tutReports",
    icon: FilePdf,
    color: "bg-rose-100 text-rose-800",
    bulletsKey: "tutReportsBullets",
  },
  {
    key: "tutOffline",
    icon: WifiSlash,
    color: "bg-slate-200 text-slate-800",
    bulletsKey: "tutOfflineBullets",
  },
  {
    key: "tutSettings",
    icon: Palette,
    color: "bg-orange-100 text-orange-800",
    bulletsKey: "tutSettingsBullets",
  },
  {
    key: "tutUsers",
    icon: UsersThree,
    color: "bg-indigo-100 text-indigo-800",
    bulletsKey: "tutUsersBullets",
  },
];

export default function LoginTutorial() {
  const { t } = useLang();
  const [open, setOpen] = useState(false);

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button
          data-testid="open-tutorial-button"
          type="button"
          variant="outline"
          className="w-full mt-3 gap-2 border-slate-300 text-slate-700 hover:bg-slate-50"
        >
          <GraduationCap size={18} weight="duotone" className="text-blue-900" />
          {t("tutorialOpen")}
        </Button>
      </DialogTrigger>
      <DialogContent
        data-testid="tutorial-dialog"
        className="max-w-2xl p-0 gap-0 overflow-hidden"
      >
        <DialogHeader className="px-6 pt-6 pb-4 border-b border-slate-200 bg-gradient-to-b from-slate-50 to-white">
          <DialogTitle className="flex items-center gap-2 text-lg text-slate-900">
            <GraduationCap size={22} weight="duotone" className="text-blue-900" />
            {t("tutorialTitle")}
          </DialogTitle>
          <DialogDescription className="text-sm text-slate-600">
            {t("tutorialSubtitle")}
          </DialogDescription>
        </DialogHeader>

        <ScrollArea className="max-h-[70vh] px-6 py-2">
          <Accordion type="single" collapsible className="w-full" defaultValue="tutDashboard">
            {MODULES.map(({ key, icon: Icon, color, bulletsKey }) => {
              const bullets = t(bulletsKey);
              const items = Array.isArray(bullets) ? bullets : [];
              return (
                <AccordionItem
                  key={key}
                  value={key}
                  data-testid={`tutorial-item-${key}`}
                  className="border-slate-200"
                >
                  <AccordionTrigger className="hover:no-underline py-4">
                    <div className="flex items-center gap-3">
                      <span
                        className={`h-9 w-9 rounded-md flex items-center justify-center ${color}`}
                      >
                        <Icon size={18} weight="duotone" />
                      </span>
                      <div className="text-left">
                        <div className="font-semibold text-slate-900 text-sm">
                          {t(`${key}Title`)}
                        </div>
                        <div className="text-xs text-slate-500">
                          {t(`${key}Sub`)}
                        </div>
                      </div>
                    </div>
                  </AccordionTrigger>
                  <AccordionContent className="pl-12 pr-2">
                    <ul className="space-y-1.5 text-sm text-slate-700">
                      {items.map((line, idx) => (
                        <li
                          key={idx}
                          data-testid={`tutorial-bullet-${key}-${idx}`}
                          className="relative pl-4 before:content-[''] before:absolute before:left-0 before:top-[9px] before:h-1.5 before:w-1.5 before:rounded-full before:bg-blue-900"
                        >
                          {line}
                        </li>
                      ))}
                    </ul>
                  </AccordionContent>
                </AccordionItem>
              );
            })}
          </Accordion>
        </ScrollArea>

        <div className="px-6 py-4 border-t border-slate-200 bg-slate-50 flex items-center justify-between gap-3">
          <p className="text-xs text-slate-500">
            {t("tutorialFooter")}
          </p>
          <Button
            data-testid="close-tutorial-button"
            onClick={() => setOpen(false)}
            className="bg-blue-900 hover:bg-blue-800"
          >
            {t("tutorialClose")}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
